import React, { useState, useRef } from 'react';
import {
  Drawer, Box, Typography, Button, CircularProgress,
  TextField, IconButton, Divider, Chip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AgentService from '../../services/AgentService';
import { useToast } from '../../contexts/ToastContext';

const REQUIRED_FIELDS = {
  create_customer: [
    { key: 'name',  label: 'Company Name' },
    { key: 'email', label: 'Email' },
  ],
  create_project: [
    { key: 'name', label: 'Project Name' },
  ],
};

const ACTION_LABELS = {
  create_customer:  'Create Customer',
  create_project:   'Create Project',
  create_invoice:   'Create Invoice',
};

// Keys that are injected automatically from previous step results — never shown as missing
const INJECTED_KEYS = {
  create_project:  ['customer', 'customerContactId'],
  create_invoice:  ['projectId'],
};

export default function InvoiceOnboardingModal({ open, onClose }) {
  const { showToast } = useToast();
  const fileInputRef = useRef(null);
  const createdCustomerIdRef = useRef(null);

  const [fileName, setFileName]         = useState('');
  const [extracting, setExtracting]     = useState(false);
  const [confirming, setConfirming]     = useState(false);
  // steps: [{ action, status: 'pending'|'active'|'done', result }]
  const [steps, setSteps]               = useState([]);
  // user-provided values for missing required fields, keyed by step index
  const [overrides, setOverrides]       = useState({});
  const [invoiceStatus, setInvoiceStatus] = useState('ISSUED');

  const activeIdx = steps.findIndex(s => s.status === 'active');
  const activeStep = activeIdx >= 0 ? steps[activeIdx] : null;

  const missingFields = activeStep
    ? (REQUIRED_FIELDS[activeStep.action.actionType] || []).filter(f => {
        if ((INJECTED_KEYS[activeStep.action.actionType] || []).includes(f.key)) return false;
        const param = activeStep.action.parameters?.[f.key];
        return !param || String(param).trim() === '';
      })
    : [];

  const canConfirm = !confirming &&
    missingFields.every(f => overrides[activeIdx]?.[f.key]?.trim());

  // ── Inject IDs from previous results into the next step's params ──────────
  const injectIds = (stepList, completedIdx, result) => {
    const updated = stepList.map((s, i) => ({ ...s, action: { ...s.action, parameters: { ...s.action.parameters } } }));

    if (completedIdx >= 0 && result?.id) {
      const completedType = stepList[completedIdx].action.actionType;

      if (completedType === 'create_customer') {
        // inject customer ID into create_project
        updated.forEach(s => {
          if (s.action.actionType === 'create_project') s.action.parameters.customer = result.id;
        });
      }
      if (completedType === 'create_project') {
        // inject project ID into create_invoice
        updated.forEach(s => {
          if (s.action.actionType === 'create_invoice') s.action.parameters.projectId = result.id;
        });
      }
    }
    return updated;
  };

  // ── File selected ─────────────────────────────────────────────────────────
  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setExtracting(true);
    setSteps([]);
    setOverrides({});
    try {
      const sequence = await AgentService.extractInvoice(file);
      if (!sequence?.length) {
        showToast('Could not extract any data from the invoice', 'warning');
        return;
      }
      setSteps(sequence.map((action, i) => ({
        action,
        status: i === 0 ? 'active' : 'pending',
        result: null,
      })));
    } catch {
      showToast('Failed to read invoice', 'error');
    } finally {
      setExtracting(false);
    }
  };

  // ── Confirm current step ──────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!activeStep) return;
    setConfirming(true);
    try {
      const params = {
        ...activeStep.action.parameters,
        ...(overrides[activeIdx] || {}),
        ...(activeStep.action.actionType === 'create_invoice' ? { status: invoiceStatus } : {}),
      };
      const result = await AgentService.execute(activeStep.action.actionType, params);
      showToast(`${ACTION_LABELS[activeStep.action.actionType] ?? 'Step'} created`, 'success');

      if (activeStep.action.actionType === 'create_customer') {
        createdCustomerIdRef.current = result?.id ?? null;
      }

      setSteps(prev => {
        let updated = prev.map((s, i) => {
          if (i === activeIdx) return { ...s, status: 'done', result };
          if (i === activeIdx + 1) return { ...s, status: 'active' };
          return s;
        });
        updated = injectIds(updated, activeIdx, result);
        return updated;
      });
      setOverrides(prev => { const n = { ...prev }; delete n[activeIdx]; return n; });

      // All done
      if (activeIdx + 1 >= steps.length) {
        showToast('Onboarding complete!', 'success');
        onClose(createdCustomerIdRef.current);
        resetState();
      }
    } catch {
      showToast('Step failed — please try again', 'error');
    } finally {
      setConfirming(false);
    }
  };

  const resetState = () => {
    setFileName('');
    setExtracting(false);
    setConfirming(false);
    setSteps([]);
    setOverrides({});
    setInvoiceStatus('ISSUED');
    createdCustomerIdRef.current = null;
  };

  const handleClose = () => {
    onClose(null);
    resetState();
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const setOverride = (stepIdx, key, value) =>
    setOverrides(prev => ({ ...prev, [stepIdx]: { ...(prev[stepIdx] || {}), [key]: value } }));

  const doneCount = steps.filter(s => s.status === 'done').length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 480 }, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #EEF2F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#1E293B' }}>
          Onboard via invoice
        </Typography>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>

        {/* Upload area */}
        {!fileName && !extracting && (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed #CBD5E1', borderRadius: 2, p: 4, textAlign: 'center',
              cursor: 'pointer', transition: 'all 0.15s',
              '&:hover': { borderColor: '#427898', bgcolor: '#F8FAFC' },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 36, color: '#94A3B8', mb: 1 }} />
            <Typography sx={{ fontSize: 13, color: '#64748B' }}>Click to upload a supplier invoice PDF</Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.5 }}>
              The AI will extract customer, project and invoice data
            </Typography>
            <input ref={fileInputRef} type="file" accept=".pdf" hidden
              onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
          </Box>
        )}

        {/* Filename + re-upload link */}
        {fileName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <UploadFileIcon sx={{ fontSize: 16, color: '#427898' }} />
            <Typography sx={{ fontSize: 13, color: '#1E293B', fontWeight: 500, flex: 1 }}>{fileName}</Typography>
            {!extracting && steps.length === 0 && (
              <Typography
                onClick={() => fileInputRef.current?.click()}
                sx={{ fontSize: 12, color: '#427898', cursor: 'pointer', textDecoration: 'underline' }}
              >
                Change
              </Typography>
            )}
            <input ref={fileInputRef} type="file" accept=".pdf" hidden
              onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
          </Box>
        )}

        {/* Extracting spinner */}
        {extracting && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#427898', mb: 2 }} />
            <Typography sx={{ fontSize: 13, color: '#64748B' }}>Reading invoice and extracting data…</Typography>
          </Box>
        )}

        {/* Steps */}
        {steps.length > 0 && (
          <>
            <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, mb: 2 }}>
              {steps.length} STEPS DETECTED · {doneCount} OF {steps.length} COMPLETE
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {steps.map((step, idx) => {
                const isDone    = step.status === 'done';
                const isActive  = step.status === 'active';
                const isPending = step.status === 'pending';
                const label     = ACTION_LABELS[step.action.actionType] ?? step.action.actionType;
                const missing   = isActive ? missingFields : [];
                const displayFields = step.action.displayFields ?? [];

                return (
                  <Box
                    key={idx}
                    sx={{
                      border: '1px solid',
                      borderColor: isDone ? '#D1FAE5' : isActive ? '#BFDBFE' : '#EEF2F6',
                      borderRadius: 2,
                      bgcolor: isDone ? '#F0FDF4' : isActive ? '#F8FAFC' : '#FAFAFA',
                      opacity: isPending ? 0.55 : 1,
                      overflow: 'hidden',
                    }}
                  >
                    {/* Step header */}
                    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isDone
                        ? <CheckCircleIcon sx={{ fontSize: 18, color: '#10B981' }} />
                        : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: isActive ? '#427898' : '#CBD5E1' }} />
                      }
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: isDone ? '#065F46' : isActive ? '#1E293B' : '#94A3B8', flex: 1 }}>
                        {idx + 1}. {label}
                      </Typography>
                      {isDone && (
                        <Chip label="Done" size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: 10, height: 20 }} />
                      )}
                      {isPending && (
                        <Chip label="Waiting" size="small" sx={{ bgcolor: '#F1F5F9', color: '#94A3B8', fontWeight: 600, fontSize: 10, height: 20 }} />
                      )}
                    </Box>

                    {/* Active step — show fields and form */}
                    {isActive && (
                      <>
                        <Divider />
                        <Box sx={{ px: 2, py: 1.5 }}>
                          {/* Extracted fields */}
                          {displayFields.filter(f => {
                            const injected = INJECTED_KEYS[step.action.actionType] || [];
                            return !injected.includes(f.key);
                          }).map((f, i) => (
                            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                              <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, minWidth: 100, flexShrink: 0, pt: 0.1 }}>
                                {f.label}
                              </Typography>
                              <Typography sx={{ fontSize: 13, color: '#1E293B', fontWeight: 500 }}>
                                {f.value || '—'}
                              </Typography>
                            </Box>
                          ))}

                          {/* Invoice status selector */}
                          {step.action.actionType === 'create_invoice' && (
                            <Box sx={{ mt: 1.5, mb: 0.5 }}>
                              <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, mb: 0.75 }}>
                                STATUS
                              </Typography>
                              <ToggleButtonGroup
                                value={invoiceStatus}
                                exclusive
                                onChange={(_, v) => v && setInvoiceStatus(v)}
                                size="small"
                              >
                                <ToggleButton value="ISSUED" sx={{ fontSize: 11, px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 600 }}>
                                  Issued
                                </ToggleButton>
                                <ToggleButton value="PAID" sx={{ fontSize: 11, px: 1.5, py: 0.5, textTransform: 'none', fontWeight: 600 }}>
                                  Paid
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Box>
                          )}

                          {/* Missing required fields */}
                          {missing.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography sx={{ fontSize: 11, color: '#EF4444', fontWeight: 600, mb: 1 }}>
                                Not found in invoice — please fill in:
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {missing.map(f => (
                                  <TextField
                                    key={f.key}
                                    label={f.label}
                                    size="small"
                                    required
                                    autoFocus
                                    value={overrides[idx]?.[f.key] ?? ''}
                                    onChange={e => setOverride(idx, f.key, e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter' && canConfirm) handleConfirm(); }}
                                    sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 }, '& label': { fontSize: 13 } }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* Confirm button */}
                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={handleConfirm}
                              disabled={!canConfirm}
                              sx={{ textTransform: 'none', fontSize: 12, bgcolor: '#427898', '&:hover': { bgcolor: '#355f79' }, minWidth: 100 }}
                            >
                              {confirming
                                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                : `Confirm`}
                            </Button>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
